import { useMemo } from 'react'
import { US_REGIONS } from '@numeo/types'
import { cn } from 'utils/cn'
import type { StateLocationData } from 'utils/location-search/LocationDataSearchV2'
import type { LocationData } from 'features/load-hub/types/filters'
import { countSelectedStates, hasState, toggleState, toggleStates } from 'features/load-hub/utils/locationSelection'

interface StateGroup {
    id: string
    label: string
    /**
     * The region's states that exist in the loaded dataset — what its count, "Select all" and "Clear" act
     * on. A region code the dataset lacks has no tile, so it is neither counted nor picked. Null for the
     * closing "Other" block (HI, PR), which has no region action.
     */
    regionStates: readonly string[] | null
    /** The tiles shown in this block — the region's states the current search left visible. */
    states: StateLocationData[]
}

/**
 * Lays the given states out region by region, keeping their order (A–Z) inside each block. States in no
 * region close the grid as "Other"; a region with nothing left to show after a search is dropped.
 */
function groupStatesByRegion(states: StateLocationData[], loadedCodes: ReadonlySet<string>): StateGroup[] {
    const groups: StateGroup[] = []
    const inRegion = new Set<string>()
    for (const region of US_REGIONS) {
        const members = new Set(region.states)
        region.states.forEach((code) => inRegion.add(code))
        const shown = states.filter((s) => members.has(s.stateCode))
        const regionStates = region.states.filter((code) => loadedCodes.has(code))
        if (shown.length > 0) groups.push({ id: region.id, label: region.label, regionStates, states: shown })
    }
    const other = states.filter((s) => !inRegion.has(s.stateCode))
    if (other.length > 0) groups.push({ id: 'other', label: 'Other', regionStates: null, states: other })
    return groups
}

/** The small uppercase heading over each block of the lane picker — "Cities", and each region. */
export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <span className="px-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{children}</span>
)

/** One state, as a code tile. */
const Tile = ({ state, selected, onClick }: { state: StateLocationData; selected: boolean; onClick: () => void }) => (
    <button
        type="button"
        title={state.stateName || state.stateCode}
        // Keep the caret in the search box: a tile click would otherwise steal focus, and the next thing
        // typed (a city, a ZIP) would go nowhere. Clicking still fires; only the focus move is suppressed.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        className={cn(
            'flex h-8 items-center justify-center rounded-lg border text-xs font-semibold transition-colors',
            selected
                ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
        )}
    >
        {state.stateCode}
    </button>
)

/**
 * A region's header: its name, how many of its states are picked, and one action for the whole region.
 * The action shows only while every state of the region is on screen — under a search that narrows the
 * block to a few tiles, "Select all" would pick states the operator cannot see.
 */
const GroupHeader = ({ group, values, onChange }: { group: StateGroup; values: LocationData[]; onChange: (next: LocationData[]) => void }) => {
    const { regionStates } = group
    const selected = regionStates ? countSelectedStates(values, regionStates) : 0
    const all = !!regionStates && selected === regionStates.length
    const wholeRegionShown = !!regionStates && group.states.length === regionStates.length
    return (
        <div className="flex items-center justify-between">
            <SectionLabel>
                {group.label}
                {regionStates && selected > 0 && (
                    <span className="ml-1 normal-case tracking-normal text-blue-600 dark:text-blue-400">
                        {selected}/{regionStates.length}
                    </span>
                )}
            </SectionLabel>
            {regionStates && wholeRegionShown && (
                <button
                    type="button"
                    aria-label={`${all ? 'Clear' : 'Select all'} ${group.label}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onChange(toggleStates(values, regionStates))}
                    className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    {all ? 'Clear' : 'Select all'}
                </button>
            )}
        </div>
    )
}

interface LanePickerStateGridProps {
    /** The states to show, A–Z — already narrowed by whatever the operator typed. */
    states: StateLocationData[]
    /** Every state the dataset loaded, unnarrowed — what a region can count and pick. */
    loadedStates: StateLocationData[]
    values: LocationData[]
    onChange: (next: LocationData[]) => void
}

/**
 * The state grid, laid out by region so a region's states always sit together and never move: picking a
 * region fills one block instead of lighting up tiles scattered across an A–Z grid.
 */
export const LanePickerStateGrid = ({ states, loadedStates, values, onChange }: LanePickerStateGridProps) => {
    const loadedCodes = useMemo(() => new Set(loadedStates.map((s) => s.stateCode)), [loadedStates])
    const groups = useMemo(() => groupStatesByRegion(states, loadedCodes), [states, loadedCodes])
    return (
        <div className="flex flex-col gap-2">
            {groups.map((group) => (
                <section key={group.id} aria-label={group.label} className="flex flex-col gap-1">
                    <GroupHeader group={group} values={values} onChange={onChange} />
                    <div className="grid grid-cols-7 gap-1">
                        {group.states.map((state) => (
                            <Tile
                                key={state.stateCode}
                                state={state}
                                selected={hasState(values, state.stateCode)}
                                onClick={() => onChange(toggleState(values, state.stateCode))}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}
